/**
 * Hook: useCustomerDocuments
 * Gerencia documentos anexados a um cliente (upload, listagem, exclusão).
 * Camada de DADOS 100% server-side via RPCs SECURITY DEFINER:
 *   - get_customer_documents
 *   - create_customer_document
 *   - delete_customer_document
 * Storage continua via SDK (bucket público "anexos").
 *
 * @version 3.0.0
 * RELEASE: 2026-05-10 — Migração para arquitetura server-side (sem .from() direto).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { showToast } from '@/lib/toast';
import { sanitizeStorageFileName } from '@/lib/storageFileName';

const logger = createLogger('CustomerDocuments');

/**
 * Sanitiza nome do arquivo removendo acentos, espaços e caracteres especiais.
 * Mesmo padrão usado em useDealAttachments, useTaskAttachments, etc.
 */
// v1.x: delegado ao utilitário central (remove emojis/acentos e evita
// o erro "Invalid key" do Supabase Storage). O nome original permanece no banco.
function sanitizeFileName(fileName: string): string {
  return sanitizeStorageFileName(fileName);
}

/** Documento do cliente (shape consumido pela UI) */
export interface CustomerDocument {
  id: string;
  tenant_id: string;
  customer_id: string;
  document_type_id: string;
  file_name: string;
  file_size: number | null;
  mime_type: string | null;
  storage_path: string;
  status: string;
  version: number;
  expires_at: string | null;
  notes: string | null;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
  /** Join virtual reconstruído a partir das colunas flat retornadas pela RPC */
  document_types?: {
    id: string;
    name: string;
    category: string;
    description: string | null;
  };
}

/** Linha bruta retornada pela RPC get_customer_documents (formato flat) */
interface CustomerDocumentRow {
  id: string;
  tenant_id: string;
  customer_id: string;
  document_type_id: string;
  file_name: string;
  file_size: number | null;
  mime_type: string | null;
  storage_path: string;
  status: string;
  version: number;
  expires_at: string | null;
  notes: string | null;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
  document_type_name: string | null;
  document_type_category: string | null;
  document_type_description: string | null;
}

/**
 * Hook para gerenciar documentos de um cliente.
 * Armazena arquivos em: anexos/customer-documents/{customerId}/{timestamp}_{fileName}
 */
export function useCustomerDocuments(customerId: string | null, isActive: boolean = true) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // ===== Listagem (server-side via RPC) =====
  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['customer-documents', customerId],
    queryFn: async (): Promise<CustomerDocument[]> => {
      if (!customerId || !tenantId) return [];

      const { data, error } = await supabase.rpc('get_customer_documents', {
        p_customer_id: customerId,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao buscar documentos do cliente:', error.message);
        throw error;
      }

      // Reconstroi o shape `document_types` aninhado para preservar a UI
      return ((data || []) as CustomerDocumentRow[]).map((row) => ({
        id: row.id,
        tenant_id: row.tenant_id,
        customer_id: row.customer_id,
        document_type_id: row.document_type_id,
        file_name: row.file_name,
        file_size: row.file_size,
        mime_type: row.mime_type,
        storage_path: row.storage_path,
        status: row.status,
        version: row.version,
        expires_at: row.expires_at,
        notes: row.notes,
        uploaded_by: row.uploaded_by,
        created_at: row.created_at,
        updated_at: row.updated_at,
        document_types: row.document_type_name
          ? {
              id: row.document_type_id,
              name: row.document_type_name,
              category: row.document_type_category || 'geral',
              description: row.document_type_description,
            }
          : undefined,
      }));
    },
    enabled: isActive && !!customerId && !!tenantId,
  });

  // ===== Upload (Storage client + INSERT via RPC) =====
  const uploadDocument = useMutation({
    mutationFn: async (input: {
      file: File;
      document_type_id: string;
      notes?: string;
      expires_at?: string;
    }) => {
      if (!customerId || !tenantId) throw new Error('IDs ausentes');

      // 1. Preparar path
      const sanitizedName = sanitizeFileName(input.file.name);
      const timestamp = Date.now();
      const storagePath = `customer-documents/${customerId}/${timestamp}_${sanitizedName}`;

      // 2. Upload no Storage (bucket público "anexos")
      const { error: uploadError } = await supabase.storage
        .from('anexos')
        .upload(storagePath, input.file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        logger.error('Erro no upload:', uploadError.message);
        throw uploadError;
      }

      // 3. Registrar metadados via RPC server-side
      const { data, error } = await supabase.rpc('create_customer_document', {
        p_payload: {
          customer_id: customerId,
          document_type_id: input.document_type_id,
          file_name: input.file.name,
          file_size: input.file.size,
          mime_type: input.file.type,
          storage_path: storagePath,
          notes: input.notes ?? null,
          expires_at: input.expires_at ?? null,
        } as any,
        p_tenant_hint: tenantId,
      });

      if (error) {
        // Rollback: remove arquivo do Storage se a RPC falhou
        await supabase.storage.from('anexos').remove([storagePath]);
        logger.error('Erro ao registrar documento:', error.message);
        throw error;
      }

      return { id: data as string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-documents', customerId] });
      showToast.success('Documento enviado com sucesso');
    },
    onError: (error: Error) => {
      showToast.error('Erro ao enviar documento', error.message);
    },
  });

  // ===== Soft delete via RPC =====
  const deleteDocument = useMutation({
    mutationFn: async (docId: string) => {
      const { error } = await supabase.rpc('delete_customer_document', {
        p_id: docId,
        p_tenant_hint: tenantId!,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-documents', customerId] });
      showToast.success('Documento removido');
    },
    onError: (error: Error) => {
      showToast.error('Erro ao remover documento', error.message);
    },
  });

  /**
   * Retorna URL pública do documento no bucket "anexos".
   * Operação puramente client-side (Storage SDK).
   */
  const getPublicUrl = (storagePath: string): string => {
    const { data } = supabase.storage.from('anexos').getPublicUrl(storagePath);
    return data.publicUrl;
  };

  return {
    documents,
    isLoading,
    uploadDocument,
    deleteDocument,
    getPublicUrl,
  };
}
